'use client';

import { ENUM_UTILS, UIMarket } from '@velocity-exchange/common';
import { useCallback, useMemo, useState } from 'react';
import {
	useCurrentAccountOrders,
	useOrdersByUserKey,
} from 'src/hooks/query/accountTradingData/useOrders';
import useIsMobileScreenSize from 'src/hooks/useIsMobileScreenSize';
import useDriftAccountStore from 'src/stores/useDriftAccountsStore';
import useShowAccountValues from '../../hooks/useShowAccountValues';
import useDriftStore from '../../stores/DriftStore/useDriftStore';
import TableStateWrapper from '../TableStateWrapper';
import { useTableFiltersState } from 'src/hooks/useTableFiltersState';
import { BalanceDisplayType } from 'src/@types/types';
import { AccountOrder } from './types';
import { OpenPerpOrderMobileCard } from './OpenPerpOrderMobileCard';
import { OpenOrdersFilters } from './OpenOrdersFilters';
import { cj } from 'src/utils/classNames';
import Checkbox from '../primitives/Checkbox/Checkbox';
import useDateDisplayState from 'src/hooks/useDateDisplayState';
import TextButton from 'src/components/primitives/TextButton/TextButton';
import ExpandAllButton from 'src/components/Buttons/ExpandAllButton';
import { MOBILE_TABLE_CARD_LIST_CLASSNAME } from 'src/components/Tables/cards/consts';
import useExpandableItemList from 'src/hooks/lists/expandable/useExpandableItemList';
import { useCancelOrders } from 'src/hooks/trade/cancel/useCancelOrders';
import { DataTable } from 'src/components/primitives/Table/DataTable';
import { getOpenOrdersColumns, OpenOrdersTableMeta } from './openOrdersColumns';
import ContentGuard from '../ContentGuard';
import MobileTableCard from 'src/components/Tables/cards/MobileTableCard';

const EMPTY_MESSAGE = 'No open orders found';

const OpenOrdersTable = (props: { overviewPage?: boolean }) => {
	const { toggleDateDisplayType, displayDatesAsTimestamp } =
		useDateDisplayState();
	const [sizeDisplayType, setSizeDisplayType] =
		useState<BalanceDisplayType>('base');

	const {
		showOnlyCurrentMarket = false,
		showPerpMarkets,
		showSpotMarkets,
		setShowOnlyCurrentMarket,
	} = useTableFiltersState(props);

	const userKey = useDriftAccountStore((s) => s.currentUserKey);
	const openOrders: AccountOrder[] = useCurrentAccountOrders();
	const ordersLoaded = useOrdersByUserKey(userKey).data != null;
	const { cancelOrders } = useCancelOrders();
	const isMobile = useIsMobileScreenSize();
	const showAccountValues = useShowAccountValues();

	const isOnTradePage = !props.overviewPage;

	const selectedMarket = useDriftStore((s) => s.selectedMarket.current);

	const filterOrders = (
		orders: AccountOrder[],
		anyFiltersApplied: boolean,
		showOnlyCurrentMarket: boolean,
		selectedMarket: UIMarket
	) => {
		if (!anyFiltersApplied) return orders;

		if (showOnlyCurrentMarket) {
			return orders.filter(
				(order) =>
					order.marketIndex === selectedMarket.market.marketIndex &&
					ENUM_UTILS.match(order.marketType, selectedMarket.marketType)
			);
		}

		return orders.filter((order) => {
			const uiMarket = new UIMarket(order.marketIndex, order.marketType);

			return (
				(showPerpMarkets && uiMarket.isPerp && !uiMarket.isPredictionMarket) ||
				(showSpotMarkets && uiMarket.isSpot)
			);
		});
	};

	const anyFiltersApplied =
		showOnlyCurrentMarket || showPerpMarkets || showSpotMarkets;

	const { displayOrders, filteredDisplayOrders } = useMemo(() => {
		if (!openOrders || !selectedMarket) {
			return { displayOrders: [], filteredDisplayOrders: [] };
		}

		const ordersForMarket = openOrders.filter(
			(order) =>
				order.marketIndex === selectedMarket.market.marketIndex &&
				ENUM_UTILS.toStr(order.marketType) == selectedMarket.marketType
		);

		const tempOrders = (() => {
			if (!ordersForMarket.length) {
				return openOrders;
			}

			return [
				...ordersForMarket,
				...(!showOnlyCurrentMarket
					? openOrders.filter(
							(order) =>
								order.marketIndex !== selectedMarket.market.marketIndex ||
								ENUM_UTILS.toStr(order.marketType) !== selectedMarket.marketType
						)
					: []),
			];
		})();

		const displayOrders = [...tempOrders].sort(
			(orderA, orderB) => orderB.slot.toNumber() - orderA.slot.toNumber()
		);

		const filteredDisplayOrders = filterOrders(
			displayOrders,
			anyFiltersApplied,
			showOnlyCurrentMarket,
			selectedMarket
		);

		return { displayOrders, filteredDisplayOrders };
	}, [
		openOrders,
		selectedMarket?.market?.symbol,
		showOnlyCurrentMarket,
		showPerpMarkets,
		showSpotMarkets,
		anyFiltersApplied,
	]);

	const {
		areAllItemsExpanded,
		handleItemExpandedChange,
		handleExpandAllItems,
		handleCollapseAllItems,
	} = useExpandableItemList({
		tableId: 'open-orders',
		numItems: filteredDisplayOrders.length,
	});

	const handleCancelAllOrCurrentOrders = useCallback(async () => {
		const ids = (filteredDisplayOrders ?? []).map((order) => order.orderId);
		if (!ids.length) {
			return;
		}

		const toastId = `cancel-filtered-orders-${ids.join(',')}-${Date.now()}`;
		await cancelOrders(toastId, ...ids);
	}, [filteredDisplayOrders, cancelOrders]);

	const columns = useMemo(
		() =>
			getOpenOrdersColumns({
				onToggleDateDisplay: toggleDateDisplayType,
			}),
		[toggleDateDisplayType]
	);

	const tableMeta: OpenOrdersTableMeta = useMemo(
		() => ({
			displayDatesAsTimestamp,
			showAccountValues,
			onCancelAll: handleCancelAllOrCurrentOrders,
		}),
		[displayDatesAsTimestamp, showAccountValues, handleCancelAllOrCurrentOrders]
	);

	const MobileMarketsControls = (
		<div className="flex w-full items-center justify-between p-3">
			{isOnTradePage ? (
				<Checkbox.Default
					id="hide-other-assets-orders"
					checked={!!showOnlyCurrentMarket}
					onCheckedChange={() =>
						setShowOnlyCurrentMarket(!showOnlyCurrentMarket)
					}
					label="Hide others"
				/>
			) : (
				<OpenOrdersFilters overviewPage={props.overviewPage} />
			)}
			<div className="flex items-center gap-x-2xl">
				<TextButton
					variant="tertiary"
					size="sm"
					onClick={handleCancelAllOrCurrentOrders}
				>
					Cancel All Orders
				</TextButton>
				{isMobile && (
					<ExpandAllButton
						areAllItemsExpanded={areAllItemsExpanded}
						totalCount={filteredDisplayOrders?.length ?? 0}
						onExpandAll={handleExpandAllItems}
						onCollapseAll={handleCollapseAllItems}
					/>
				)}
			</div>
		</div>
	);

	if (isMobile) {
		return (
			<div className={cj(props.overviewPage && 'mb-4')} id="open_orders_table">
				<TableStateWrapper
					requireAccountCreated
					records={displayOrders ?? []}
					emptyStateText={
						<div className="flex items-center justify-center">
							No open orders found
						</div>
					}
					requireWalletConnect
					id="open_orders_table"
				>
					<div className="bg-main-bg">{MobileMarketsControls}</div>
					<div
						className={cj(
							'w-full sm:max-h-full',
							MOBILE_TABLE_CARD_LIST_CLASSNAME
						)}
					>
						{filteredDisplayOrders.length > 0 ? (
							filteredDisplayOrders.map((order) => (
								<OpenPerpOrderMobileCard
									key={order.orderId}
									order={order}
									showAccountValues={showAccountValues}
									areAllItemsExpanded={areAllItemsExpanded}
									handleItemExpandedChange={handleItemExpandedChange}
									sizeDisplayType={sizeDisplayType}
									onToggleSizeDisplayType={() =>
										setSizeDisplayType(
											sizeDisplayType === 'base' ? 'notional' : 'base'
										)
									}
								/>
							))
						) : (
							<MobileTableCard.EmptyPlaceholder>
								{EMPTY_MESSAGE}
							</MobileTableCard.EmptyPlaceholder>
						)}
					</div>
				</TableStateWrapper>
			</div>
		);
	}

	return (
		<ContentGuard requireAccount requireWallet isLoading={!ordersLoaded}>
			<DataTable
				columns={columns}
				data={filteredDisplayOrders}
				emptyMessage={EMPTY_MESSAGE}
				columnPinning={{ right: ['edit'] }}
				meta={tableMeta}
				dataTestId="open-orders-table"
			/>
		</ContentGuard>
	);
};

export default OpenOrdersTable;
